import {
  InvoiceStatus,
  IPayableSchema
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";

import {
  toWSDateString,
  WSActions,
  WSElement,
  WSSectionToolbar,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import queryString from "qs";
import { useHistory } from "react-router-dom";
import { calculateLineItemsTotal } from "../../../Invoices/utils";
import { usePayablesQuery } from "../../../Invoices/components/NewPayablesTable/PayablesTable";
import { WSQueries } from "../../../query/WSQuery";
import { buildPayeeDetailsPath } from "../utils";
import { getStatusColumn } from "../../../Invoices/components/NewPayablesTable/getStatusColumn";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

type RecentPayablesWidgetProps = {
  payeeId: string;
};

export const RecentPayablesWidget: React.FC<RecentPayablesWidgetProps> = ({
  payeeId
}) => {
  const history = useHistory();
  const userId = useUserId();
  const featureFlags = useFeatureFlags();
  // The payables list reads its contractor filter differently per experience:
  // the new module (flag on) expects `filter[payeeId]` (parsed by useUrlQuery),
  // while the legacy table expects a top-level `payeeId`. Match the rendered one.
  const useNewPayables = !!featureFlags.data?.payablesMaterializedPagination;

  const queryPayables = usePayablesQuery(
    {
      payeeId
    },
    {
      size: 5
    }
  );

  const queryPayrollSettings = useQueryPayrollSettings(userId);

  return (
    <WSQueries queries={{ queryPayables, queryPayrollSettings }}>
      {({ queryPayablesData, queryPayrollSettingsData }) => {
        if (queryPayablesData.data.length === 0) {
          return null;
        }

        return (
          <WSElement>
            <WSSectionToolbar title="Recent payables" mb="S" />

            <WSTable<IPayableSchema>
              tableData={queryPayablesData.data.map(payable => ({
                id: payable.payableId,
                data: payable
              }))}
              onRowClick={row => {
                history.push({
                  pathname: `/member/invoices/payables/all/${row.id}`,
                  state: { backPath: buildPayeeDetailsPath(payeeId) }
                });
              }}
              columns={[
                {
                  config: {
                    header: {
                      text: "Invoice #"
                    },
                    gridTemplateSizeMax: "1fr"
                  },
                  renderFunction: ({ data }) => {
                    const date =
                      data.status === InvoiceStatus.Paid
                        ? data.events.paidAt
                        : data.events.openedAt;

                    const amountString = formatMoney(
                      calculateLineItemsTotal(data.lineItems) +
                        (data.chargedFees?.lateFee?.amount || 0),
                      data.currency
                    );

                    const dateString = date
                      ? toWSDateString(date, "monthDayYear")
                      : undefined;

                    const secondaryText = [amountString, dateString]
                      .filter(Boolean)
                      .join(" • ");

                    return (
                      <WSTableCell
                        text={`#${data.invoiceNumber ?? "Pending"}`}
                        secondaryText={secondaryText}
                      />
                    );
                  }
                },
                {
                  config: {
                    header: {
                      text: "Status"
                    },
                    gridTemplateSizeMax: "1.2fr"
                  },
                  renderFunction: ({ data }) => {
                    return (
                      <WSTableCell
                        {...getStatusColumn(data, queryPayrollSettingsData)}
                      />
                    );
                  }
                }
              ]}
            />
            <WSActions
              alignment="left"
              size="M"
              mt="M"
              buttons={[
                {
                  label: "View all",
                  kind: "Link",
                  onClick: () => {
                    const query = queryString.stringify(
                      useNewPayables ? { filter: { payeeId } } : { payeeId }
                    );
                    history.push(`/member/invoices/payables/all?${query}`);
                  }
                }
              ]}
            />
          </WSElement>
        );
      }}
    </WSQueries>
  );
};
